import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeftRight, Plus, Loader2, Trash2, Search, Zap, ChevronRight } from "lucide-react";
import { TradeContactCard, TradeRequests } from "@/components/TradeRequests";

const TYPES = {
  have: { label: "Has a spot", short: "HAVE", cls: "bg-[#3B6B1E]/15 text-[#3B6B1E]" },
  want: { label: "Wants a spot", short: "WANT", cls: "bg-[#1D4ED8]/12 text-[#1D4ED8]" },
};

export default function TradeList() {
  const m = useIsMobile();
  const nav = useNavigate();
  const [trades, setTrades] = useState(null);
  const [matches, setMatches] = useState([]);
  const [rodeos, setRodeos] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [posting, setPosting] = useState(false);
  const [me, setMe] = useState(null);

  const load = () => {
    api.get("/trades").then((r) => setTrades(r.data)).catch(() => setTrades([]));
    api.get("/trades/matches").then((r) => setMatches(r.data)).catch(() => setMatches([]));
  };
  useEffect(() => {
    load();
    api.get("/rodeos").then((r) => setRodeos(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const del = async (id) => {
    try { await api.delete(`/trades/${id}`); toast.success("Trade removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    let list = trades || [];
    if (typeFilter) list = list.filter((t) => t.type === typeFilter);
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      list = list.filter((t) => (t.rodeo_name || "").toLowerCase().includes(n)
        || (t.event_category || "").toLowerCase().includes(n)
        || (t.user_name || "").toLowerCase().includes(n));
    }
    return list;
  }, [trades, typeFilter, q]);

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const headBorder = m ? "border-white/10" : "border-[#E4E7EB]";

  return (
    <div className={page} data-testid="tradelist-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center justify-between gap-3 ${headBorder}`}>
        <div className="flex items-center gap-2">
          <ArrowLeftRight className="w-6 h-6 text-[#C8000F]" />
          <div>
            <h1 className={`font-display font-bold text-xl ${title}`}>Trade List</h1>
            <p className="text-xs text-slate-400">{(trades || []).length} open post{(trades || []).length === 1 ? "" : "s"}{matches.length ? ` · ${matches.length} possible match${matches.length === 1 ? "" : "es"}` : ""}</p>
          </div>
        </div>
        <button onClick={() => setPosting(true)} data-testid="trade-post-btn" className="flex items-center gap-1.5 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg">
          <Plus className="w-4 h-4" /> Post a trade
        </button>
      </div>

      <div className="px-4 sm:px-6 py-4 max-w-3xl space-y-4">
        <TradeContactCard m={m} />
        <TradeRequests m={m} />
        {/* Matches */}
        {matches.length > 0 && (
          <div>
            <div className={`text-[11px] font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5 ${m ? "text-slate-500" : "text-slate-400"}`}><Zap className="w-3.5 h-3.5 text-[#E68A2E]" /> Possible matches</div>
            <div className="space-y-2">
              {matches.map((mt, i) => (
                <div key={i} className={`rounded-xl border p-3 ${card} ${mt.exact ? "ring-1 ring-[#E68A2E]/40" : ""}`} data-testid={`trade-match-${i}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`font-display font-bold text-sm ${title}`}>{mt.rodeo_name}</span>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-[#F1F3F5] text-slate-600">{mt.event_category}</span>
                    {mt.exact && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#E68A2E]/15 text-[#E68A2E]">EXACT</span>}
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="flex-1 truncate"><b className="text-[#3B6B1E]">{mt.have.user_name}</b> has {mt.have.performance_label}</span>
                    <ArrowLeftRight className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="flex-1 truncate text-right"><b className="text-[#1D4ED8]">{mt.want.user_name}</b> wants {mt.want.performance_label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`flex items-center gap-2 rounded-lg border px-3 flex-1 min-w-[200px] ${card}`}>
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rodeo, event or rider" data-testid="trade-search"
              className={`flex-1 bg-transparent py-2.5 text-sm focus:outline-none ${m ? "text-slate-100 placeholder:text-slate-500" : "text-[#14181F] placeholder:text-slate-400"}`} />
          </div>
          <div className="flex gap-1.5">
            {[{ v: "", l: "All" }, { v: "have", l: "Haves" }, { v: "want", l: "Wants" }].map((t) => (
              <button key={t.v} onClick={() => setTypeFilter(t.v)} data-testid={`trade-filter-${t.v || "all"}`}
                className={`text-xs font-bold px-3 py-2 rounded-lg ${typeFilter === t.v ? "bg-[#C8000F] text-white" : m ? "bg-white/5 text-slate-300" : "bg-[#F1F3F5] text-slate-600"}`}>{t.l}</button>
            ))}
          </div>
        </div>

        {/* Open posts */}
        {trades === null ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : filtered.length === 0 ? (
          <div className={`rounded-xl border border-dashed p-8 text-center ${m ? "border-white/15" : "border-[#D7DBE0]"}`}>
            <ArrowLeftRight className="w-8 h-8 mx-auto mb-3 text-[#C8000F]" />
            <div className={`font-display font-bold ${title}`}>No trades posted yet</div>
            <p className="text-sm text-slate-400 mt-1 mb-4">Post that you have a performance spot to give up, or that you want to move into one.</p>
            <button onClick={() => setPosting(true)} data-testid="trade-empty-post" className="text-sm font-semibold px-3 py-2 rounded-lg bg-[#C8000F]/10 text-[#C8000F]">+ Post a trade</button>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((t) => {
              const ty = TYPES[t.type] || TYPES.have;
              const mine = me && t.user_id === me.id;
              return (
                <div key={t.id} className={`rounded-xl border p-3 ${card}`} data-testid={`trade-post-${t.id}`}>
                  <div className="flex items-start gap-3">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-md shrink-0 ${ty.cls}`}>{ty.short}</span>
                    <div className="flex-1 min-w-0">
                      <button onClick={() => t.rodeo_id && nav(`/rodeo/${t.rodeo_id}?tab=trade`)} className="text-left w-full">
                        <div className={`font-display font-bold text-sm truncate flex items-center gap-1 ${title}`}>{t.rodeo_name} <ChevronRight className="w-3.5 h-3.5 text-slate-300" /></div>
                      </button>
                      <div className="text-xs text-slate-400 mt-0.5">
                        <span className="font-semibold">{t.event_category}</span> · {ty.label} · {t.performance_label}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {t.user_name || "A rider"}{t.created_at ? ` · ${fmtDate(t.created_at.slice(0, 10))}` : ""}
                      </div>
                      {t.note ? <div className={`text-xs mt-1.5 rounded-md px-2 py-1.5 ${m ? "bg-white/5 text-slate-300" : "bg-[#F7F8FA] text-slate-600"}`}>{t.note}</div> : null}
                    </div>
                    {mine && <button onClick={() => del(t.id)} data-testid={`trade-delete-${t.id}`} className="text-slate-300 hover:text-[#C8000F] shrink-0"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PostTradeDialog open={posting} onClose={() => setPosting(false)} rodeos={rodeos} m={m} onPosted={load} />
    </div>
  );
}

function PostTradeDialog({ open, onClose, rodeos, m, onPosted }) {
  const [rodeoId, setRodeoId] = useState("");
  const [event, setEvent] = useState("");
  const [perf, setPerf] = useState("");
  const [type, setType] = useState("have");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const rodeo = rodeos.find((r) => r.id === rodeoId);
  const events = rodeo?.events || [];
  const perfs = rodeo?.performances || [];

  const submit = async () => {
    if (!rodeoId) return toast.error("Pick a rodeo");
    if (!event) return toast.error("Pick an event");
    if (!perf.trim()) return toast.error("Enter the performance");
    setSaving(true);
    try {
      await api.post("/trades", { rodeo_id: rodeoId, event_category: event, performance_label: perf.trim(), type, note: note.trim() });
      toast.success("Trade posted");
      setRodeoId(""); setEvent(""); setPerf(""); setNote(""); setType("have");
      onPosted();
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const inp = m ? "bg-white/5 border-white/10 text-slate-100" : "bg-white border-[#D7DBE0] text-[#14181F]";
  const DLG = m ? "bg-[#12161C] text-slate-100 border-white/10" : "bg-white text-[#14181F] border-[#E4E7EB]";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={DLG} data-testid="trade-post-dialog">
        <DialogHeader><DialogTitle className="font-display">Post a trade</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {[{ v: "have", l: "I have a spot" }, { v: "want", l: "I want a spot" }].map((t) => (
              <button key={t.v} onClick={() => setType(t.v)} data-testid={`trade-type-${t.v}`}
                className={`p-2.5 rounded-lg border text-sm font-semibold ${type === t.v ? "border-[#C8000F] text-[#C8000F]" : m ? "border-white/10 text-slate-300" : "border-[#E4E7EB] text-slate-600"}`}>{t.l}</button>
            ))}
          </div>
          <label className="block text-sm">Rodeo
            <select value={rodeoId} onChange={(e) => { setRodeoId(e.target.value); setEvent(""); setPerf(""); }} data-testid="trade-rodeo" className={`w-full mt-1 rounded-lg border px-3 py-2.5 ${inp}`}>
              <option value="">Select a rodeo…</option>
              {rodeos.map((r) => <option key={r.id} value={r.id} className={m ? "bg-[#12161C]" : ""}>{r.name}</option>)}
            </select>
          </label>
          <label className="block text-sm">Event
            <select value={event} onChange={(e) => setEvent(e.target.value)} data-testid="trade-event" className={`w-full mt-1 rounded-lg border px-3 py-2.5 ${inp}`} disabled={!rodeoId}>
              <option value="">{rodeoId ? "Select event…" : "Pick a rodeo first"}</option>
              {events.map((e) => <option key={e} value={e} className={m ? "bg-[#12161C]" : ""}>{e}</option>)}
            </select>
          </label>
          <label className="block text-sm">Performance
            {perfs.length > 0 ? (
              <select value={perf} onChange={(e) => setPerf(e.target.value)} data-testid="trade-perf" className={`w-full mt-1 rounded-lg border px-3 py-2.5 ${inp}`} disabled={!rodeoId}>
                <option value="">Select performance…</option>
                {perfs.map((p) => <option key={p.id} value={p.label} className={m ? "bg-[#12161C]" : ""}>{p.label}{p.date ? ` (${fmtDate(p.date)})` : ""}</option>)}
              </select>
            ) : (
              <input value={perf} onChange={(e) => setPerf(e.target.value)} placeholder="e.g. Saturday 7pm perf" data-testid="trade-perf-input" className={`w-full mt-1 rounded-lg border px-3 py-2.5 text-sm ${inp}`} />
            )}
          </label>
          <label className="block text-sm">Note (optional)
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. need to be out by Sat noon" data-testid="trade-note" className={`w-full mt-1 rounded-lg border px-3 py-2.5 text-sm ${inp}`} />
          </label>
          <button onClick={submit} disabled={saving} data-testid="trade-submit" className="w-full flex items-center justify-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Post trade
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
